/**
 * Kismet Quiz V2 — Validation
 * Screen-based validation for the V2 wizard (see quiz.js for the state shape).
 */

// ── Email ────────────────────────────────────────────────────────────────
// Three layers, none of which asks the shopper to type the address twice:
//  1. normalizeEmail  — trims, lowercases, drops the trailing punctuation
//                       mobile keyboards add and a space stuck to the `@`.
//                       Never rewrites anything else: an inner space stays
//                       and fails validation, so the shopper sees it.
//  2. isValidEmail    — RFC-shaped syntax: dot-atom local part (no leading,
//                       trailing or doubled dots), labelled domain, alphabetic
//                       TLD of 2+ letters. Rejects only what cannot be an
//                       address; `gmail.comj` still passes here.
//  3. suggestEmail    — Mailcheck-style "did you mean": edit distance against
//                       the domains and TLDs Kismet actually sees. Sized on
//                       Completed Quiz emails Aug 18–Sep 21 2026: 4 typos in
//                       470 (icloud.om, yahoo.clm, yahoo.coma, gmail.comc),
//                       all caught by this layer.

const KNOWN_DOMAINS = [
 'gmail.com', 'yahoo.com', 'hotmail.com', 'aol.com', 'comcast.net', 'me.com',
 'icloud.com', 'sbcglobal.net', 'outlook.com', 'msn.com', 'att.net', 'live.com',
 'verizon.net', 'mac.com', 'ymail.com', 'cox.net', 'bellsouth.net', 'charter.net',
 'earthlink.net', 'optonline.net', 'rocketmail.com', 'googlemail.com',
 'protonmail.com', 'protonmail.ch', 'proton.me', 'pm.me', 'hey.com', 'duck.com',
 'gmx.com', 'gmx.de', 'mail.com', 'zoho.com',
 // Real country-code variants of the big providers, so a typo in the name
 // part can still be corrected without ever changing the TLD.
 'hotmail.fr', 'hotmail.de', 'hotmail.es', 'hotmail.it', 'hotmail.co.uk', 'hotmail.ca', 'hotmail.com.mx', 'hotmail.com.br',
 'yahoo.fr', 'yahoo.de', 'yahoo.es', 'yahoo.it', 'yahoo.co.uk', 'yahoo.ca', 'yahoo.com.mx', 'yahoo.com.br',
 'outlook.fr', 'outlook.de', 'outlook.es', 'outlook.it', 'outlook.co.uk', 'outlook.com.br',
 'live.fr', 'live.de', 'live.it', 'live.co.uk', 'live.ca', 'live.com.mx'
];
// Second-level names strong enough to correct the TLD on their own
// (`gmail.comj` → `gmail.com`) without a full-domain match.
const KNOWN_SLDS = ['gmail', 'yahoo', 'hotmail', 'outlook', 'icloud', 'aol', 'comcast', 'protonmail', 'live', 'msn', 'me', 'att', 'sbcglobal', 'verizon'];
// Providers that issue mailboxes under exactly ONE domain: for these, any
// other TLD is a typo, 2-letter ones included (`icloud.om` → icloud.com,
// `comcast.ne` → comcast.net). The value is the canonical domain — several
// are .net, so never build it as `sld + '.com'`. hotmail / yahoo / outlook /
// aol / live / proton run real country-code domains and are NOT here.
const SINGLE_DOMAIN_SLDS = {
 gmail: 'gmail.com', icloud: 'icloud.com', me: 'me.com', msn: 'msn.com',
 comcast: 'comcast.net', sbcglobal: 'sbcglobal.net', verizon: 'verizon.net', att: 'att.net'
};
const KNOWN_TLDS = ['com', 'net', 'org', 'edu', 'gov', 'co', 'io', 'me', 'us', 'ca', 'uk', 'co.uk', 'mx', 'info', 'biz'];

function normalizeEmail(raw) {
 if (!raw) return '';
 let e = String(raw).trim().toLowerCase();
 e = e.replace(/^mailto:/, '');
 e = e.replace(/\s*@\s*/, '@');        // "ana @gmail.com" (keyboard space after @)
 e = e.replace(/[.,;:]+$/, '');          // "ana@gmail.com." from autocorrect
 return e;
}

const LOCAL_RE = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;
const LABEL_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

function isValidEmail(email) {
 const e = normalizeEmail(email);
 if (!e || e.length > 254) return false;
 const at = e.lastIndexOf('@');
 if (at < 1 || e.indexOf('@') !== at) return false;
 const local = e.slice(0, at);
 const domain = e.slice(at + 1);
 if (local.length > 64 || !LOCAL_RE.test(local)) return false;
 const labels = domain.split('.');
 if (labels.length < 2) return false;
 if (!labels.every(l => LABEL_RE.test(l))) return false;
 return /^[a-z]{2,}$/.test(labels[labels.length - 1]);
}

// Damerau-Levenshtein (optimal string alignment): one transposition counts as
// a single edit, so `gmial` is 1 away from `gmail`.
function editDistance(a, b) {
 const d = [];
 for (let i = 0; i <= a.length; i++) { d[i] = [i]; }
 for (let j = 0; j <= b.length; j++) { d[0][j] = j; }
 for (let i = 1; i <= a.length; i++) {
  for (let j = 1; j <= b.length; j++) {
   const cost = a[i - 1] === b[j - 1] ? 0 : 1;
   d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
   if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
    d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
   }
  }
 }
 return d[a.length][b.length];
}

function closest(value, candidates, maxDistance) {
 let best = null;
 let bestDist = maxDistance + 1;
 candidates.forEach(c => {
  const dist = editDistance(value, c);
  if (dist < bestDist) { bestDist = dist; best = c; }
 });
 return best;
}

/**
 * "Did you mean" for the domain part. Returns the corrected full address or
 * null when the domain is known or nothing close enough exists.
 * Never fires for a domain that is itself in the known list, so a real
 * `yahoo.ca` is not "corrected" to `yahoo.com`.
 */
function suggestEmail(email) {
 const e = normalizeEmail(email);
 const at = e.lastIndexOf('@');
 if (at < 1) return null;
 const local = e.slice(0, at);
 const domain = e.slice(at + 1);
 if (KNOWN_DOMAINS.indexOf(domain) > -1) return null;

 // A 2-letter TLD is (almost always) a real country code, and one edit can
 // turn it into another real one (`hotmail.fr` → `hotmail.ca`). So when the
 // shopper typed a 2-letter TLD, every candidate must keep that TLD: only the
 // name before it is ever corrected (`hotmal.fr` → `hotmail.fr`). Exception:
 // SINGLE_DOMAIN_SLDS, where a 2-letter TLD can only be a typo. Review 2026-09-23.
 const dot = domain.indexOf('.');
 const sld = dot > 0 ? domain.slice(0, dot) : domain;
 const tld = dot > 0 ? domain.slice(dot + 1) : '';
 const single = Object.prototype.hasOwnProperty.call(SINGLE_DOMAIN_SLDS, sld) ? SINGLE_DOMAIN_SLDS[sld] : null;
 const ccTld = tld.length === 2 && !single;

 // Whole domain, fuzzy. Only against long candidates (8+ chars): on short
 // ones a single edit is ambiguous (`abl.com` is not `aol.com`, `mx.com` is
 // not `me.com`). 2 edits only for 10+ char inputs (`hotmal.cmo`).
 // Single-domain provider with any other TLD: the answer is known outright.
 if (dot > 0 && single && domain !== single) return local + '@' + single;

 const candidates = KNOWN_DOMAINS.filter(d => d.length >= 8 && (!ccTld || d.endsWith('.' + tld)));
 let fixed = closest(domain, candidates, domain.length >= 10 ? 2 : 1);

 // TLD only: a famous second-level name with a mangled 3+ letter TLD
 // (`gmail.con`, `aol.comm`), or any domain with a mangled 3+ letter TLD
 // (`kismetpets.con`). Never touches a 2-letter TLD (see above).
 if (!fixed && dot > 0 && !ccTld && KNOWN_TLDS.indexOf(tld) === -1 && (KNOWN_SLDS.indexOf(sld) > -1 || tld.length >= 3)) {
  const fixedTld = closest(tld, KNOWN_TLDS, 1);
  if (fixedTld) fixed = sld + '.' + fixedTld;
 }

 if (!fixed || fixed === domain) return null;
 return local + '@' + fixed;
}

function isValidRequired(value) {
 if (value === null || value === undefined) return false;
 if (typeof value === 'string') return value.trim() !== '';
 if (typeof value === 'number') return value !== 0 && !isNaN(value);
 if (Array.isArray(value)) return value.length > 0;
 return !!value;
}

/**
 * Get validation errors for a V2 screen.
 * @param {String} screen - 'start' | 'basics' | 'lifestage' | 'recipe' | 'gate'
 * @param {Object} dog - Current dog state
 * @param {Object} state - Full quiz state ({ dogs, email, ... })
 * @returns {Array} error messages
 */
function getScreenErrors(screen, dog, state) {
 const errors = [];

 switch (screen) {
  case 'start': {
   const dogs = state.dogs || [];
   if (dogs.length === 0) {
    errors.push('Add at least one dog');
   } else if (dogs.some(d => !d.name || d.name.trim() === '')) {
    errors.push(dogs.length > 1 ? 'Every pup needs a name' : 'What is your dog’s name?');
   }
   break;
  }

  case 'basics':
   if (!isValidRequired(dog && dog.breed)) {
    errors.push('We couldn’t match that breed — try picking one from the list. Mixed Breed totally counts!');
   }
   const w = dog ? parseFloat(dog.weight) : 0;
   if (!w || w < 1 || w > 250) {
    errors.push('Weight should be between 1 and 250 lbs');
   }
   if (!isValidRequired(dog && dog.bodyCondition)) {
    errors.push('Tap the body shape that looks most familiar');
   }
   // sex / spayNeuter are optional per PRD
   break;

  case 'lifestage':
   // Full birthday required (design decision 2026-07-27) — it drives real age
   if (!dog || !isValidRequired(dog.birthdayMonth) || !isValidRequired(dog.birthdayDay) || !isValidRequired(dog.birthdayYear)) {
    errors.push('When was your pup born? A guess is fine!');
   }
   if (!isValidRequired(dog && dog.activity)) {
    errors.push('How active is your pup?');
   }
   if (!isValidRequired(dog && dog.motivation)) {
    errors.push('Tell us why you’re switching food');
   }
   // "Something else" needs no extra input — the textarea it reveals is
   // optional (client call 2026-08-05: it's just an escape hatch, low friction)
   break;

  case 'recipe':
   if (!dog || !Array.isArray(dog.healthGoals) || dog.healthGoals.length === 0) {
    errors.push('Pick at least one thing to support');
   }
   if (!dog || !Array.isArray(dog.allergies) || dog.allergies.length === 0) {
    errors.push('Select allergies, or “None”');
   }
   // currentFoodType/Brand questions removed 2026-08-06 (client not using
   // the data) — no requirement here anymore
   break;

  case 'gate':
   if (!state.email || !state.email.trim()) {
    errors.push('Drop in your email to reveal the plan');
   } else if (!isValidEmail(state.email)) {
    errors.push('That email doesn’t look quite right — double-check it?');
   }
   break;
 }

 return errors;
}

if (typeof window !== 'undefined') {
 window.QuizValidator = {
  normalizeEmail,
  isValidEmail,
  suggestEmail,
  isValidRequired,
  getScreenErrors
 };
}
